// Story 6.9 (ADO-1163, AC3) — Banner. Figma 609:49.
//
// Four types map to the Figma variants + the Phosphor icon each uses:
//   warning → Warning(AlertTriangle) · info → Info(AlertCircle)
//   success → CheckCircle · error → XCircle.
// a11y (NFR24–26): status/success/info use role="status" (polite); warning/error
// use role="alert" (assertive). Optional dismiss button carries an aria-label.
//
// Story 8.3 (ADO-2012), Task 13.4 (card 10): `layout`. `inline` (the default, unchanged): the icon
// beside the title over the text. `stacked` (Figma "Order sent banner", `Layout="Stacked"`): the
// icon beside the title, then the text and an optional full-width action each on their own row,
// the same at every width (the side panel). `adaptive` (the cart review page, decided with the
// user): stacked below 768px, and from 768px card 09's inline box with the action at the far
// right at its natural width.

import type { ReactNode } from 'react';
import { Button } from '../Button/Button';
import { Icon, type IconName } from '../Icon/Icon';
import styles from './Banner.module.css';

/** ADO-3985: `offer` is Banner Type=Offer, the incomplete multi-buy flag (14, 19). */
export type BannerType = 'warning' | 'info' | 'success' | 'error' | 'offer';
export type BannerLayout = 'inline' | 'stacked' | 'adaptive';

export interface BannerAction {
  readonly label: string;
  readonly onClick: () => void;
}

const ICON_BY_TYPE: Record<BannerType, IconName> = {
  warning: 'warning',
  info: 'info',
  success: 'checkCircle',
  error: 'xCircle',
  offer: 'gift',
};

export interface BannerProps {
  type?: BannerType;
  title?: ReactNode;
  children?: ReactNode;
  /** When provided, renders a dismiss (X) button that calls this handler. */
  onDismiss?: () => void;
  className?: string;
  /** Story 8.3 (ADO-2012): the layout (default `inline`). */
  layout?: BannerLayout;
  /** Story 8.3 (ADO-2012): Figma `Action=true` on the stacked and adaptive layouts: a primary
   *  button after the text. */
  action?: BannerAction;
}

export function Banner({
  type = 'info',
  title,
  children,
  onDismiss,
  className,
  layout = 'inline',
  action,
}: BannerProps) {
  const assertive = type === 'warning' || type === 'error';
  if (layout !== 'inline') {
    return (
      <div
        className={[styles.banner, styles[type], className].filter(Boolean).join(' ')}
        role={assertive ? 'alert' : 'status'}
        data-layout={layout}
        data-has-action={action === undefined ? undefined : ''}
        tabIndex={-1}
      >
        <Icon name={ICON_BY_TYPE[type]} size={18} className={styles.stackedIcon} />
        {title ? <span className={`${styles.title} ${styles.stackedTitle}`}>{title}</span> : null}
        {children ? (
          <span className={`${styles.supporting} ${styles.stackedBody}`}>{children}</span>
        ) : null}
        {action === undefined ? null : (
          <Button
            // A host hands focus to it by this attribute (the Button takes no ref).
            data-banner-action=""
            variant="primary"
            fullWidth
            className={styles.action}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        )}
      </div>
    );
  }
  return (
    <div
      className={[styles.banner, styles[type], className].filter(Boolean).join(' ')}
      role={assertive ? 'alert' : 'status'}
      data-layout="inline"
    >
      <Icon name={ICON_BY_TYPE[type]} size={18} className={styles.icon} />
      <div className={styles.text}>
        {title ? <span className={styles.title}>{title}</span> : null}
        {children ? <span className={styles.supporting}>{children}</span> : null}
      </div>
      {onDismiss ? (
        <button type="button" className={styles.dismiss} aria-label="Dismiss" onClick={onDismiss}>
          <Icon name="x" size={16} />
        </button>
      ) : null}
    </div>
  );
}
